// src/tabs/messages/JournalPreviewModal.jsx — Onglet Journal (ADR-268).
//
// Aperçu du message EXACT pour UNE résa : clic sur une ligne du Journal
// (vue À venir OU Historique) → variables remplacées avec les vraies valeurs,
// dans la LANGUE de la résa, blocs conditionnels résolus.
//
// RÉUTILISE le moteur d'aperçu existant de MessageEditor : renderTemplate
// (src/lib/template-variables, mode texte par défaut — sortie affichée en
// texte React, jamais de HTML injecté → pas de XSS possible sur un prénom
// voyageur forgé). Même limitation assumée que le preview MessageEditor :
// les liens ({livret_url}, {welcome_form_url}, {paiement_caution_url}) sont
// générés AU MOMENT de l'envoi — jamais ici (zéro token JIT).
// Fix stabilisation 2026-07 (même famille que #1003) : en mode texte,
// renderTemplate remplaçait ces 3 variables par '' (resolveValue rend une
// chaîne vide sans ctx serveur) → le lien DISPARAISSAIT silencieusement de
// l'aperçu et le hint « générés à l'envoi » ne s'affichait jamais. On décore
// donc le ctx d'aperçu avec withPreviewLinkPlaceholders (placeholder honnête
// « [lien généré à l'envoi] », partagé avec les aperçus de l'éditeur).
//
// Vue Historique : si message_logs.body est stocké (sent / sent_dry_run /
// failed), on affiche CE contenu — la vérité de ce qui est parti (ou serait
// parti en dry-run) — jamais un re-rendu. Pour les traces de skip (sans
// body), aperçu recalculé avec l'état ACTUEL, étiqueté honnêtement.

import { X, Eye, Mail, Info } from "lucide-react"
import { DS } from "../../lib/ui"
import { renderTemplate } from "../../lib/template-variables"
import { getAllLogsFlat } from "../../lib/sousloc-helpers"
import { withPreviewLinkPlaceholders, PREVIEW_LINK_PLACEHOLDER } from "./preview-link-placeholders"

const LANG_LABEL = { fr: "Français", en: "Anglais", es: "Espagnol" }

// Une variable {x} encore présente dans le rendu = lien/donnée générée à
// l'envoi seulement (ex. livret_url) — on prévient plutôt que d'inventer.
const UNRESOLVED_RE = /\{[a-z0-9_]+\}/i

export default function JournalPreviewModal({ mode, row, message, cfg, onClose }) {
  const reservation = row.reservation || null
  const lang = (reservation && reservation.guest_language) || "fr"
  const logement = reservation
    ? getAllLogsFlat(cfg).find(l => l.id === reservation.logement_id) || null
    : null

  // Contenu : body stocké prioritaire (Historique) sinon re-rendu (À venir /
  // traces de skip avec résa connue).
  const stored = mode === "history" && row.body
  let subject = null
  let body = null
  if (stored) {
    subject = row.subject || null
    body = row.body
  } else if (message && reservation) {
    // Placeholder « [lien généré à l'envoi] » sur les 3 variables de liens
    // (sinon renderTemplate les rend '' et elles disparaissent en silence).
    const ctx = withPreviewLinkPlaceholders({ reservation, logement, cfg, language: lang })
    const subjectTpl = message[`subject_${lang}`] || message.subject_fr || ""
    const bodyTpl = message[`body_${lang}`] || message.body_fr || ""
    subject = subjectTpl ? renderTemplate(subjectTpl, ctx) : null
    body = bodyTpl ? renderTemplate(bodyTpl, ctx) : null
  }

  // Hint « générés à l'envoi » : placeholder de lien OU variable {x} restée
  // non résolue dans le rendu recalculé (jamais sur un body STOCKÉ — il dit
  // la vérité de ce qui est parti).
  const hasUnresolved = !stored && !!body &&
    (body.includes(PREVIEW_LINK_PLACEHOLDER) || UNRESOLVED_RE.test(body))
  const guestName = reservation
    ? `${reservation.guest_first_name || "?"} ${reservation.guest_last_name || ""}`.trim()
    : null

  return (
    <div data-testid="journal-preview-modal"
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:50,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflow:"auto"}}
      onClick={onClose}>
      <div className="fbz-modal-sheet" onClick={e => e.stopPropagation()}
        style={{background:DS.card,borderRadius:14,padding:22,maxWidth:640,width:"100%",maxHeight:"90vh",overflow:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,gap:10}}>
          <div style={{minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:DS.fontDisplay,fontSize:16,fontWeight:600,color:DS.marineDark}}>
              <Eye size={17} strokeWidth={2} color={DS.marine}/> {row.send?.name || row.message_name || "Aperçu du message"}
            </div>
            <div style={{fontSize:11.5,color:DS.textSecondary,marginTop:3,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
              {guestName && <span>Pour <strong>{guestName}</strong></span>}
              {logement && <span>· {logement.nom || logement.id}</span>}
              {reservation && <span>· séjour {reservation.start_date} → {reservation.end_date}</span>}
              <span style={{fontSize:10,fontWeight:700,color:DS.marine,background:DS.cielSoft,padding:"1px 7px",borderRadius:8}}>
                {LANG_LABEL[lang] || lang}
              </span>
            </div>
          </div>
          <button data-testid="journal-preview-close" onClick={onClose} aria-label="Fermer"
            style={{background:"transparent",border:"none",cursor:"pointer",color:DS.textMuted,display:"inline-flex",flexShrink:0}}>
            <X size={20} strokeWidth={2}/>
          </button>
        </div>

        {/* Provenance du contenu — toujours dit, jamais implicite */}
        <div style={{display:"flex",gap:7,alignItems:"flex-start",padding:"7px 11px",marginBottom:12,background:stored?DS.successBg:DS.cielSoft,borderRadius:9,fontSize:11,color:stored?DS.successText:DS.marine,lineHeight:1.5}}>
          <Info size={13} strokeWidth={2} style={{flexShrink:0,marginTop:2}}/>
          <span>
            {stored
              ? "Contenu tracé au moment de l'envoi — exactement ce qui est parti (ou serait parti en simulation)."
              : mode === "history"
                ? "Aperçu recalculé avec l'état actuel de la résa — rien n'est parti pour cette trace."
                : "Aperçu du message tel qu'il partirait pour cette résa."}
            {hasUnresolved && ` Les liens (livret, caution, formulaire) sont générés au moment de l'envoi — ils apparaissent ici sous la forme « ${PREVIEW_LINK_PLACEHOLDER} ».`}
          </span>
        </div>

        {(subject || body) ? (
          <div style={{background:"#fff",border:"1px dashed #dde3ec",borderRadius:8,padding:14}}>
            {subject && (
              <div style={{display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:700,color:DS.marineDark,marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${DS.border}`}}>
                <Mail size={14} strokeWidth={2}/> Sujet : {subject}
              </div>
            )}
            <div data-testid="journal-preview-body"
              style={{fontSize:13,color:"#333",lineHeight:1.6,whiteSpace:"pre-wrap",fontFamily:DS.fontBody}}>
              {body || <span style={{color:"#aaa",fontStyle:"italic"}}>(contenu vide)</span>}
            </div>
          </div>
        ) : (
          <div style={{padding:"16px 14px",background:DS.pageBg,borderRadius:8,fontSize:12.5,color:DS.textSecondary,textAlign:"center"}}>
            Pas de contenu disponible pour cette trace (message ou résa introuvable).
          </div>
        )}

        {mode === "history" && row.destinataire && (
          <div style={{marginTop:10,fontSize:11,color:DS.textMuted}}>
            Destinataire : <code style={{fontFamily:"monospace",fontSize:10.5,background:DS.pageBg,padding:"1px 6px",borderRadius:5}}>{row.destinataire}</code>
          </div>
        )}
      </div>
    </div>
  )
}
